import React, { useMemo } from "react";
import { memoizeFunction, mergeStyles } from "@fluentui/react";
import { micSpeakingLottieJson } from "./micSpeaking.lottie.json";
import Lottie from "lottie-react";
import { MicFilled, MicOffFilled } from "@fluentui/react-icons";
import { FluentIconsProps } from "@fluentui/react-icons";


interface MicrophoneIconProps {
    size?: number;
    muted?: boolean;
    speaking?: boolean;
    className?: string;
}


const micIconStyles = memoizeFunction((size: number) => ({
    fullSize: {
        width: `${size}px`,
        height: `${size}px`,
    },

    root: {
        display: "inline-block",

        '--mic-icon-size': `${size}px`,
        '--mic-icon-font-size': `calc(var(--mic-icon-size) * 0.3)`,
        '--container-size-full': `calc(var(--mic-icon-size) * 1)`,
        '--container-size-smaller': `calc(var(--mic-icon-size) * 0.5)`,

        width: "var(--mic-icon-size)",
        height: "var(--mic-icon-size)",
    },

    outerContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        width: "var(--mic-icon-size)",
        height: "var(--mic-icon-size)",
    },

    container: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",


        '&.muted': {
            backgroundColor: "#000000",
            opacity: 0.5,
            width: "var(--container-size-smaller)",
            height: "var(--container-size-smaller)",
        },

        '&.unmuted': {
            backgroundColor: "#307FE2",
            width: "var(--container-size-smaller)",
            height: "var(--container-size-smaller)",
        },

        '&.speaking': {
            backgroundColor: "transparent",
            width: "var(--container-size-full)",
            height: "var(--container-size-full)",
        },
    },

    svgIcon: {
        // NOTE: svg icon font size = width/height.
        //             Needs to be reduced as the svg icons are larger than the lottie animation.
        color: "white",
        width: "var(--mic-icon-font-size)",
        height: "var(--mic-icon-font-size)",
        lineHeight: "var(--mic-icon-font-size)",
    },

    lottieIcon: {
        width: "var(--mic-icon-size)",
        height: "var(--mic-icon-size)",
        display: "inline-block",
    },
}));


/**
 * Microphone Icon with states/indicators for
 * - muted
 * - unmuted
 * - speaking
 *
 * Examples:
 *                                 <MicrophoneIcon muted={true} />
 *                                 <MicrophoneIcon muted={false} />
 *                                 <MicrophoneIcon muted={false} speaking={true} />
 *                                 <MicrophoneIcon muted={false} />
 *                                 <br/><br/>
 *                                 <MicrophoneIcon muted={true} size={24} />
 *                                 <MicrophoneIcon muted={false} size={24} />
 *                                 <MicrophoneIcon muted={false} speaking={true} size={24} />
 *                                 <MicrophoneIcon muted={false} size={24} />
 *                                 <br/><br/>
 *                                 <MicrophoneIcon muted={true} size={78} />
 *                                 <MicrophoneIcon muted={false} size={78} />
 *                                 <MicrophoneIcon muted={false} speaking={true} size={78} />
 *                                 <MicrophoneIcon muted={false} size={78} />
 *
 * @param props
 * @constructor
 */
export const MicrophoneIcon = (props: MicrophoneIconProps): JSX.Element => {

    const size = props.size || 50;
    const styles = micIconStyles(size);

    const className = useMemo(
        () => props.muted ? 'muted' : props.speaking ? 'speaking' : 'unmuted',
        [props.muted, props.speaking]
    );

    const icon = useMemo(() => {
        if (!props.muted && props.speaking) {
            return (
                <Lottie animationData={micSpeakingLottieJson} className={mergeStyles(styles.lottieIcon)} />
            );
        }

        const iconProps: FluentIconsProps = {
            className: mergeStyles(styles.svgIcon),
        };

        return (
            <>
                {props.muted ? <MicOffFilled {...iconProps} /> : <MicFilled {...iconProps} />}
            </>
        );
    }, [props.muted, props.speaking, styles.svgIcon, styles.lottieIcon]);

    return (
        <div className={mergeStyles(styles.root, props.className)}>
            <div className={mergeStyles(styles.outerContainer)}>
                <div className={mergeStyles(className, styles.container)}>
                    {icon}
                </div>
            </div>
        </div>
    );
}

